<template>
    <section :class="[$style['mds-section'], classObject]" :data-mds-version="version">
        <div
            :tabindex="tabIndex"
            :class="[$style['mds-section__header-container'], headerClasses, sizeClass]"
            v-on="headerEvents"
            @keydown.enter="toggle($event)"
            :aria-expanded="ariaExpanded"
            :aria-controls="collapsible ? expandedId : null"
            :role="collapsible ? 'button' : null"
        >
            <mds-icon
                v-if="collapsible"
                name="triangle-fill-right"
                :class="$style['mds-section__toggle']"
                :size="iconSize"
            />
            <component :is="headingType" :class="$style['mds-section__title']">
                <slot name="mds-section-title">{{ title }}</slot>
                <div :class="$style['mds-section__title-description']" v-if="hasTitleDescription">
                    <slot name="mds-section-title-description"></slot>
                </div>
            </component>
            <div :class="$style['mds-section__supplemental-content']" v-if="$slots['mds-section-supplemental-content']">
                <slot name="mds-section-supplemental-content"></slot>
            </div>
            <div @click.stop :class="$style['mds-section__actions']" v-if="$slots['mds-section-actions']">
                <slot name="mds-section-actions"></slot>
            </div>
        </div>
        <div
            v-show="!collapsible || (open && collapsible)"
            :class="$style['mds-section__content']"
            :id="collapsible ? expandedId : null"
        >
            <template v-if="content">
                <span v-html="content"></span>
            </template>
            <template v-else>
                <slot />
            </template>
        </div>
    </section>
</template>

<script>
    import { MdsButton } from '@mds/button-vue3';
    import MdsIcon from '@mds/icon-vue3';
    import { MdsUniqueId } from '@mds/utils-js-vue3';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsSection',
        components: {
            MdsButton,
            MdsIcon,
        },
        data() {
            return {
                open: this.expanded,
                version: `${name}-${version}`,
            };
        },
        emits: ['mds-section-expanded', 'mds-section-closed'],
        watch: {
            expanded(newVal) {
                if (this.open !== newVal) {
                    this.toggle();
                }
            },
        },
        mixins: [MdsUniqueId],
        props: {
            bold: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, sets the section heading text to bold.',
                },
            },
            border: {
                type: String,
                default: 'primary',
                validator(value) {
                    return ['primary', 'secondary', 'tertiary', 'none'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `primary`, `secondary`, `tertiary`, `none`.',
                    description: 'Sets section heading border style.',
                },
            },
            borderPosition: {
                type: String,
                default: 'top',
                validator(value) {
                    return ['top', 'bottom'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `top`, `bottom`.',
                    description: 'Sets section heading border position.',
                },
            },
            container: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, renders a visible container around a section.',
                },
            },
            content: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the section content.',
                    support: ['v-html'],
                },
            },
            collapsible: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, enables the ability to collapse a section.',
                },
            },
            expanded: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, expands a collapsible section.',
                },
            },
            headingLevel: {
                type: Number,
                default: 2,
                validator(value) {
                    return value >= 1 && value <= 6;
                },
                docs: {
                    validation: '—',
                    description: 'Sets the `<h#>` section heading level of the card title.',
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            size: {
                type: Number,
                default: 5,
                validator(value) {
                    return value >= 1 && value <= 9;
                },
                docs: {
                    validation: 'Number between `1` and `9`.',
                    description:
                        'Sets the section heading text size, based on [typography heading level styles](/visual-language/typography).',
                },
            },
            title: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the section section heading text.',
                },
            },
        },
        computed: {
            ariaExpanded() {
                if (!this.collapsible) {
                    return null;
                }
                return this.open ? 'true' : 'false';
            },
            headingType() {
                return `h${this.headingLevel}`;
            },
            classObject() {
                const obj = {};

                obj[this.$style['mds-component--on-dark']] = this.onDark;
                obj[this.$style['mds-section--expanded']] = this.open;
                obj[this.$style['mds-section--collapsible']] = this.collapsible;
                obj[this.$style['mds-section--container']] = this.container;
                return obj;
            },
            headerClasses() {
                const obj = {};

                obj[this.$style['mds-section--border-bottom']] = this.borderPosition === 'bottom' || this.container;
                obj[this.$style[`mds-section--${this.border}`]] = this.border;
                obj[this.$style['mds-section--font-bold']] = this.bold;

                return obj;
            },
            sizeClass() {
                return this.$style[`mds-section--level-${this.size}`];
            },
            iconSize() {
                return this.size < 6 ? 'medium' : 'small';
            },
            tabIndex() {
                return this.collapsible ? 0 : null;
            },
            expandedId() {
                return this.generateUuid('mds-section');
            },
            hasTitleDescription() {
                return !!this.$slots['mds-section-title-description'];
            },
            headerEvents() {
                return this.collapsible ? { click: this.toggle } : {};
            },
        },
        methods: {
            toggle(event) {
                if (this.collapsible) {
                    if (event) {
                        // if triggered by user action, invert value
                        this.open = !this.open;
                        this.$emit(`mds-section-${this.open ? 'expanded' : 'closed'}`, event);
                    } else {
                        // if set via prop, assign prop value
                        this.open = this.expanded;
                        this.$emit(`mds-section-${this.open ? 'expanded' : 'closed'}`);
                    }
                }
            },
        },
    };
</script>

<style lang="scss" module>
    @use './section.scss';
</style>
